import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../Button/Button';
import ModalWarning from '../Modal/ModalWarning';
import { AttentionMessage } from '../AttentionMessage/AttentionMessage';
import { RightArrow } from '../../../icons/SvgIcons';
import { useAppSelector } from '../../../hooks/redux';
import { selectIsSuperAdmin } from '../../../store/selectors/authSelectors';

export interface TabItem {
  key: string;
  label: string;
  content: ReactNode;
  hasUnsavedChanges?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  basePath: string;
  showWarningModal?: boolean;
  pendingNavigation?: string | null;
  onNavigationWarning?: (targetPath: string) => void;
  onConfirmNavigation?: () => void;
  onResetChanges?: () => void;
}

export function Tabs({
  tabs,
  basePath,
  showWarningModal = false,
  pendingNavigation = null,
  onNavigationWarning,
  onConfirmNavigation,
  onResetChanges
}: TabsProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdminRole = useAppSelector(selectIsSuperAdmin);

  const visibleTabs = isSuperAdminRole
    ? tabs.filter((tab) => tab.key !== 'general')
    : tabs;

  const activeTabKey =
    visibleTabs.find((tab) =>
      location.pathname.includes(`${basePath}/${tab.key}`)
    )?.key || visibleTabs[0]?.key;

  const activeTab = visibleTabs.find((tab) => tab.key === activeTabKey);

  const { hasMajorSeverity } = useAppSelector((state) => state.aiSystems);

  const showAttentionMessage = hasMajorSeverity && basePath !== '/settings';

  const handleTabClick = (tabKey: string) => {
    const targetPath = `${basePath}/${tabKey}`;
    const currentTab = visibleTabs.find((tab) => tab.key === activeTabKey);

    if (currentTab?.hasUnsavedChanges && activeTabKey !== tabKey) {
      if (onNavigationWarning) {
        onNavigationWarning(targetPath);
      }
    } else {
      navigate(targetPath);
    }
  };

  const handleConfirmExit = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
      if (onResetChanges) {
        onResetChanges();
      }
    }
    if (onConfirmNavigation) {
      onConfirmNavigation();
    }
  };

  const getButtonStyle = (isActive: boolean) => {
    return [
      'px-3 py-2 text-sm font-medium',
      isActive
        ? 'bg-purple-100 rounded-md text-purple-700 cursor-default'
        : 'text-gray-550'
    ].join(' ');
  };

  return (
    <div className="w-full flex-col flex-1">
      {showAttentionMessage && (
        <div className="mb-4">
          <AttentionMessage
            action={
              activeTab?.key !== 'monitoring-audit-logging' ? (
                <Link
                  to={`${basePath}/monitoring-audit-logging`}
                  className="flex items-center gap-1 text-sm text-rose-800 font-medium"
                >
                  View Modifications{RightArrow('currentColor')}
                </Link>
              ) : null
            }
          />
        </div>
      )}
      <div className="flex gap-8 mb-4">
        {visibleTabs.map((tab) => (
          <Button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            buttonClassName={getButtonStyle(tab.key === activeTabKey)}
          >
            {tab.label}
          </Button>
        ))}
      </div>
      {activeTab?.content}
      {showWarningModal && (
        <ModalWarning
          title="Exit Without Saving?"
          description="Are you sure you want to exit? All of your data will be lost and changes will not be made."
          handleButton={handleConfirmExit}
        />
      )}
    </div>
  );
}
